import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Search, MapPin, DollarSign, Clock, AlertTriangle, Map, List } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix for default marker icons in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons by urgency
const createCustomIcon = (urgency) => {
  const colors = {
    normal: '#3B82F6',     // blue
    urgent: '#F59E0B',      // amber
    emergency: '#EF4444'    // red
  };
  
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${colors[urgency] || colors.normal};
      width: 30px;
      height: 30px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 3px solid white;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30]
  });
};

// Bogotá neighborhoods coordinates
const BOGOTA_LOCATIONS = {
  'Chapinero': { lat: 4.6517, lng: -74.0608 },
  'Usaquén': { lat: 4.6951, lng: -74.0304 },
  'Kennedy': { lat: 4.6286, lng: -74.1616 },
  'Suba': { lat: 4.7418, lng: -74.0833 },
  'Engativá': { lat: 4.7055, lng: -74.1112 },
  'Fontibón': { lat: 4.6752, lng: -74.1440 },
  'Teusaquillo': { lat: 4.6455, lng: -74.0859 },
  'La Candelaria': { lat: 4.5961, lng: -74.0726 },
  'Bosa': { lat: 4.5877, lng: -74.1970 },
  'Ciudad Bolívar': { lat: 4.5193, lng: -74.1534 },
  'default': { lat: 4.7110, lng: -74.0721 }
};

const getLocationCoords = (locationStr) => {
  if (!locationStr) return BOGOTA_LOCATIONS.default;
  
  for (const [name, coords] of Object.entries(BOGOTA_LOCATIONS)) {
    if (locationStr.toLowerCase().includes(name.toLowerCase())) {
      return coords;
    }
  }
  
  // Add some random offset to avoid overlapping markers
  return {
    lat: BOGOTA_LOCATIONS.default.lat + (Math.random() - 0.5) * 0.05,
    lng: BOGOTA_LOCATIONS.default.lng + (Math.random() - 0.5) * 0.05
  };
};

export default function BrowseJobs({ user, token }) {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedUrgency, setSelectedUrgency] = useState('all');
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'list'

  const categories = [
    { value: 'all', label: 'Todas las categorías' },
    { value: 'plomeria', label: 'Plomería' },
    { value: 'electricidad', label: 'Electricidad' },
    { value: 'pintura', label: 'Pintura' },
    { value: 'albanileria', label: 'Albañilería' },
    { value: 'ingeniero_civil', label: 'Ingeniero Civil' },
    { value: 'arquitecto', label: 'Arquitecto' },
    { value: 'topografia', label: 'Topografía' },
    { value: 'cerrajeria', label: 'Cerrajería' },
    { value: 'planos_disenos', label: 'Planos y Diseños' },
    { value: 'licencias', label: 'Licencias' }
  ];

  const urgencies = [
    { value: 'all', label: 'Todas las urgencias' },
    { value: 'normal', label: 'Normal' },
    { value: 'urgent', label: 'Urgente' },
    { value: 'emergency', label: 'Emergencia' }
  ];

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    filterJobs();
  }, [jobs, searchTerm, selectedCategory, selectedUrgency]);

  const fetchJobs = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/jobs/available`, config);
      setJobs(response.data);
      setFilteredJobs(response.data);
    } catch (error) {
      toast.error('Error al cargar trabajos');
    } finally {
      setLoading(false);
    }
  };

  const filterJobs = () => {
    let filtered = [...jobs];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(job =>
        job.title?.toLowerCase().includes(search) ||
        job.description?.toLowerCase().includes(search)
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(job => job.category === selectedCategory);
    }

    if (selectedUrgency !== 'all') {
      filtered = filtered.filter(job => job.urgency === selectedUrgency);
    }

    setFilteredJobs(filtered);
  };

  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  const getUrgencyConfig = (urgency) => {
    const config = {
      normal: { color: 'bg-slate-100 text-slate-700', label: 'Normal', icon: Clock },
      urgent: { color: 'bg-yellow-100 text-yellow-700', label: 'Urgente', icon: AlertTriangle },
      emergency: { color: 'bg-red-100 text-red-700', label: 'Emergencia', icon: AlertTriangle }
    };
    return config[urgency] || config.normal;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedUrgency('all');
  };

  // Prepare jobs with coordinates for map
  const jobsWithCoords = filteredJobs.map(job => ({
    ...job,
    coords: getLocationCoords(job.location?.address)
  }));

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-[#3A4F6C] text-white p-4 sticky top-0 z-[1000]">
        <div className="container mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-bold">TRABAJOS DISPONIBLES</h1>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              data-testid="search-input"
              placeholder="Buscar por actividad o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white text-slate-900"
            />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b shadow-sm p-4 sticky top-[120px] z-[999]">
        <div className="container mx-auto space-y-3">
          <div className="flex gap-2">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedUrgency} onValueChange={setSelectedUrgency}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Urgencia" />
              </SelectTrigger>
              <SelectContent>
                {urgencies.map(urg => (
                  <SelectItem key={urg.value} value={urg.value}>
                    {urg.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* View Toggle */}
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-500">
              {filteredJobs.length} trabajo{filteredJobs.length !== 1 ? 's' : ''} disponible{filteredJobs.length !== 1 ? 's' : ''}
            </span>
            <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
              <Button
                variant={viewMode === 'map' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('map')}
                className={viewMode === 'map' ? 'bg-orange-500 hover:bg-orange-600' : ''}
              >
                <Map className="h-4 w-4 mr-1" />
                Mapa
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-orange-500 hover:bg-orange-600' : ''}
              >
                <List className="h-4 w-4 mr-1" />
                Lista
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto p-4">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-500">Cargando trabajos...</p>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg mb-4">No se encontraron trabajos disponibles</p>
            <Button variant="outline" onClick={clearFilters}>
              Limpiar filtros
            </Button>
          </div>
        ) : viewMode === 'map' ? (
          /* Map View */
          <div className="rounded-xl overflow-hidden shadow-lg border-2 border-slate-200" style={{ height: '60vh' }}>
            <MapContainer
              center={[4.7110, -74.0721]}
              zoom={12}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {jobsWithCoords.map((job) => (
                <Marker
                  key={job.id}
                  position={[job.coords.lat, job.coords.lng]}
                  icon={createCustomIcon(job.urgency)}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      <h3 className="font-bold text-lg mb-2">{job.title}</h3>
                      <p className="text-sm text-slate-600 mb-2">{job.description?.substring(0, 100)}...</p>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-orange-100 text-orange-700">
                          {getCategoryLabel(job.category)}
                        </Badge>
                        <Badge className={getUrgencyConfig(job.urgency).color}>
                          {getUrgencyConfig(job.urgency).label}
                        </Badge>
                      </div>
                      <div className="flex items-center text-green-600 font-bold mb-3">
                        <DollarSign className="h-4 w-4" />
                        ${job.budget?.toLocaleString()} COP
                      </div>
                      <Button
                        size="sm"
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                        onClick={() => navigate(`/job/${job.id}`)}
                      >
                        Ver Detalles
                      </Button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
            
            {/* Legend */}
            <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3 z-[1000]">
              <p className="text-xs font-semibold mb-2">Urgencia:</p>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span>Normal</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span>Urgente</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span>Emergencia</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* List View */
          <div className="space-y-4">
            {filteredJobs.map((job) => {
              const urgencyConfig = getUrgencyConfig(job.urgency);
              const UrgencyIcon = urgencyConfig.icon;
              
              return (
                <Card
                  key={job.id}
                  data-testid={`job-card-${job.id}`}
                  className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-orange-500"
                  onClick={() => navigate(`/job/${job.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-slate-800 mb-1">
                          {job.title}
                        </h3>
                        <p className="text-sm text-slate-600 line-clamp-2">
                          {job.description}
                        </p>
                      </div>
                      <Badge className={urgencyConfig.color}>
                        <UrgencyIcon className="h-3 w-3 mr-1" />
                        {urgencyConfig.label}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-4 mt-3">
                      <Badge className="bg-orange-100 text-orange-700">
                        {getCategoryLabel(job.category)}
                      </Badge>
                      
                      {job.location?.address && (
                        <span className="text-sm text-slate-500 flex items-center">
                          <MapPin className="h-4 w-4 mr-1" />
                          {job.location.address}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex justify-between items-center mt-3 pt-3 border-t">
                      <span className="text-lg font-bold text-green-600">
                        ${job.budget?.toLocaleString()} COP
                      </span>
                      <Button
                        size="sm"
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                      >
                        Ver Detalles
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Custom CSS for map markers */}
      <style>{`
        .custom-marker {
          background: transparent;
          border: none;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 12px;
        }
        .leaflet-popup-content {
          margin: 12px;
        }
      `}</style>
    </div>
  );
}
